import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { fetchAndActivate } from 'firebase/remote-config';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { useLocalStorage, useOpenPopup, useShowBuyBalancePage } from '@recargas-dominicanas/core/hooks';
import { AuthApi, BusinessApi } from '@recargas-dominicanas/core/api';
import { isAuth, useAsyncEffect } from '@recargas-dominicanas/core/utils';
import { UserProfile } from '../../components/user-profile/user-profile.component';
import { ErrorPage } from '../../components/error-page/error-page.component';
import logo from '../../images/recargas-dominicanas.svg';
import { app, remoteConfig } from '../../firebase';

import {
  useBusiness,
  useBusinessUsers,
  useSalesReport,
  useProducts,
  useTransactions,
  useSales
} from '@recargas-dominicanas/core/store';

import {
  Header, 
  Navbar,
  NavbarOption 
} from '@recargas-dominicanas/core/components';

import { style } from './main.module.css';

export function Main() {

  const navigate = useNavigate();

  const businessStore = useBusiness();
  const businessUserStore = useBusinessUsers();
  const salesStore = useSales();
  const salesReportStore = useSalesReport();
  const productsStore = useProducts();
  const transactionsStore = useTransactions();

  const business = businessStore.get();
  const businessUser = businessUserStore.get();
  const openProfile = useOpenPopup();
  const { onboardingCompleted } = useLocalStorage();
  const showBuyBalancePage = useShowBuyBalancePage(remoteConfig);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const isMobile = window.matchMedia('(max-width: 800px)').matches;

  if (process.env.APP_REDIRECT_TO_MOBILE_APP === 'true' && isMobile) {
    window.location.href = process.env.APP_MOBILE_APP_URL!;
    return;
  }

  useAsyncEffect(async () => {

    const auth = await isAuth('businessUser');

    if (!auth) {
      return navigate('/iniciar-sesion');
    }

    try {

      await Promise.all([
        businessStore.fetch(),
        businessUserStore.fetch(),
        productsStore.fetch(),
        fetchAndActivate(remoteConfig).catch(console.error),
      ]);

      setLoading(false);

      salesStore.fetch();
      salesReportStore.fetchCurrent();
      salesReportStore.fetchPage(1, 50);
      transactionsStore.fetchGroupByDay(1, 50);
      transactionsStore.fetchPage(1, 50);

    } catch (err) {

      if (err instanceof AxiosError && (err.response?.status === 401 || err.response?.status === 403)) {
        await AuthApi.logout();
      }

      setError(true);
      setLoading(false);
    }
  });

  useEffect(() => {

    const messaging = getMessaging(app);

    getToken(messaging, { vapidKey: process.env.APP_FIREBASE_MESSAGING_KEY })
      .then((token) => {
        if (token) {
          BusinessApi
            .addNotificationToken(token)
            .catch(console.error);
        } else {
          Notification.requestPermission();
        }
      })
      .catch(reason => console.error(reason));

    onMessage(messaging, payload => {
      if (payload.data?.type === 'BALANCE') {
        salesReportStore.fetchCurrent();
        alert(`Balance asignado: ${payload.data.balance}`);
      }
    });

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        salesReportStore.fetchCurrent();
      }
    });
  }, []);

  useEffect(() => {
    const ticketWidth = localStorage.getItem('ticketWidth');
    const endLines = localStorage.getItem('endLines');
    if (!ticketWidth) localStorage.setItem('ticketWidth', '40');
    if (!endLines) localStorage.setItem('endLines', '5');
  }, []);

  if (isLoading) {
    return null;
  }

  if (error) {
    return <ErrorPage/>;
  }

  if (business.customer.status === 'new') {
    if (onboardingCompleted) {
      return <Navigate to={showBuyBalancePage ? '/como-comprar-balance' : '/contactar-ventas'}/>;
    } else {
      return <Navigate to='/onboarding'/>;
    }
  }

  return (
    <div>
      <Header
        logo={logo}
        title='Recargas Dominicanas'
        name={`${businessUser.name}, ${business.name}`} 
        onOpenProfile={openProfile.handleOpen}/>
      <div className={style.body}>
        <Navbar style={style.navBar}>
          <NavbarOption align='top' icon='storefront' label='Inicio' route='/'/>
          <NavbarOption align='top' icon='receipt_long' label='Transacciones' route='/transacciones'/>
          <NavbarOption align='top' icon='point_of_sale' label='Cierres de venta' route='/cierre-de-ventas'/>
          <NavbarOption align='top' icon='data_usage' label='Ventas' route='/ventas'/>
          <NavbarOption align='top' icon='account_balance_wallet' label='Comprar balance' route='/comprar-balance'/>
          <NavbarOption align='bottom' icon='help_outline' label='Ayuda' route='/ayuda'/>
          <NavbarOption align='bottom' icon='settings' label='Configuración' route='/configuracion'/>
        </Navbar>
        <div className={style.content}>
          <Outlet/>
        </div>
      </div>
      <UserProfile 
        open={openProfile.open}
        onClose={openProfile.handleClose}
        user={businessUser}
        business={business!}/>
    </div>
  );
}
